'use client'

import '@rainbow-me/rainbowkit/styles.css'

import React, { ReactNode } from 'react'

import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { uniq } from 'lodash'
import dynamic from 'next/dynamic'
import { toast } from 'sonner'

import { AppContainer } from '~/app/_root/AppContainer'
import { ChainRegistryGate } from '~/app/_root/ChainRegistryGate'
import { QueryClientProvider } from '~/app/_root/QueryClientProvider'
import { Toaster } from '~/app/_root/Toaster'
import { AuthProvider } from '~/context/AuthProvider'
import { SplitsProvider } from '~/context/SplitsProvider'
import { WalletConnectProvider } from '~/context/WalletConnectProvider'
import { ExperimentalStylingProvider } from '~/context/experimentalStylingContext'
import { NotificationProvider } from '~/context/notificationContext'
import { useDevtoolsVisible } from '~/hooks/useDevtoolsVisible'
import { usePWATracking } from '~/hooks/usePWATracking'
import { useSplitsConnectTracking } from '~/hooks/useSplitsConnectTracking'
import { useCookieStore } from '~/store/cookieStore'
import { useIsMobile } from '~/utils/devices'
import { initTracking } from '~/utils/tracking'

// Dynamic imports with ssr: false to avoid indexedDB errors during SSR
// WalletConnect's ethereum-provider accesses indexedDB at module load
const PrivyProviderWrapper = dynamic(
  () =>
    import('~/context/externalContext').then((mod) => mod.PrivyProviderWrapper),
  { ssr: false },
)
const WagmiProviderWrapper = dynamic(
  () =>
    import('~/context/externalContext').then((mod) => mod.WagmiProviderWrapper),
  { ssr: false },
)
const RainbowProvider = dynamic(
  () => import('~/context/externalContext').then((mod) => mod.RainbowProvider),
  { ssr: false },
)

if (typeof window !== 'undefined') initTracking()

export function App({ children }: { children: ReactNode }) {
  const isMobile = useIsMobile()
  const devtoolsVisible = useDevtoolsVisible()

  // Hydrate cookie store once on mount - reads document.cookie once
  React.useEffect(() => {
    useCookieStore.getState().hydrate()
  }, [])

  useConsumeSessionToastMessages()
  usePWATracking()

  return (
    <QueryClientProvider>
      <SplitsConnectTracking />
      {devtoolsVisible && <ReactQueryDevtools initialIsOpen={false} />}
      {!devtoolsVisible && (
        // Next.js renders its dev indicator inside a shadow-DOM `nextjs-portal`,
        // so the whole element has to be hidden from outside — this also hides
        // error overlays while devtools are hidden. No-op outside `next dev`
        // since the portal is only mounted by the dev server.
        <style>{'nextjs-portal { display: none !important; }'}</style>
      )}
      <ChainRegistryGate>
        <PrivyProviderWrapper>
          <WagmiProviderWrapper>
            <RainbowProvider>
              <AuthProvider>
                <Toaster isMobile={isMobile} />
                <NotificationProvider>
                  <SplitsProvider>
                    <WalletConnectProvider>
                      <ExperimentalStylingProvider>
                        <AppContainer>{children}</AppContainer>
                      </ExperimentalStylingProvider>
                    </WalletConnectProvider>
                  </SplitsProvider>
                </NotificationProvider>
              </AuthProvider>
            </RainbowProvider>
          </WagmiProviderWrapper>
        </PrivyProviderWrapper>
      </ChainRegistryGate>
    </QueryClientProvider>
  )
}

const SplitsConnectTracking = () => {
  useSplitsConnectTracking()
  return null
}

const useConsumeSessionToastMessages = () => {
  const toastMessages = useCookieStore((s) => s.toastMessages)
  const removeToastMessages = useCookieStore((s) => s.removeToastMessages)

  React.useEffect(
    function consumeSessionToastMessages() {
      if (toastMessages && toastMessages.length > 0) {
        // Using uniq because the first render in development runs this effect twice
        const uniqueToastMessages = uniq(toastMessages)
        uniqueToastMessages.forEach((message: string) => {
          toast.success(message)
        })
        removeToastMessages()
      }
    },
    [removeToastMessages, toastMessages],
  )
}
